import type { RoomLayoutSpot, RoomLayoutSpotTypeItem } from './RoomLayoutMap.types';
import { SpotStatus } from './RoomLayoutMap.types';
import { resolveSystemSpotTypeKey } from './nonBookableSpotTypeIcons';
import { hasSpotTypeName, hasSpotTypePrefix } from './spotTypeGuards';

function layoutSpotTypeForNames(
  spotTypes: RoomLayoutSpotTypeItem[] | undefined,
  ...names: (string | undefined)[]
): RoomLayoutSpotTypeItem | undefined {
  if (!spotTypes?.length) return undefined;
  for (const name of names) {
    const trimmed = name?.trim();
    if (!trimmed) continue;
    const item = spotTypes.find((t) => t.name.trim().toLowerCase() === trimmed.toLowerCase());
    if (item?.prefix.trim()) return item;
  }
  return undefined;
}

/** Prefixes that map to bundled Trainer/Fan icons — never guess these from spot labels alone. */
const INFERRED_SYSTEM_PREFIXES = new Set(['tr', 'fn']);

export function inferNotBookablePrefixFromDisplayName(
  name: string | undefined,
  spotLabel: string | undefined
): string | undefined {
  const firstWord = name?.trim().split(/\s+/)[0];
  if (!firstWord || firstWord.length !== 2) return undefined;
  if (INFERRED_SYSTEM_PREFIXES.has(firstWord.toLowerCase())) return undefined;
  const label = spotLabel?.trim();
  if (label && firstWord.toLowerCase() === label.toLowerCase()) return undefined;
  return firstWord;
}

export function enrichSpotsWithLayoutSpotTypePrefix(
  spots: RoomLayoutSpot[],
  spotTypes: RoomLayoutSpotTypeItem[] | undefined
): RoomLayoutSpot[] {
  const prefixByTypeId = new Map<string, string>();
  const nameByTypeId = new Map<string, string>();
  for (const spot of spots) {
    if (hasSpotTypePrefix(spot)) {
      prefixByTypeId.set(spot.spot_type_id, spot.spot_type_prefix.trim());
    }
    if (hasSpotTypeName(spot)) {
      nameByTypeId.set(spot.spot_type_id, spot.spot_type_name.trim());
    }
  }

  return spots.map((spot) => {
    if (hasSpotTypePrefix(spot)) return spot;

    const fromTypeId = prefixByTypeId.get(spot.spot_type_id);
    if (fromTypeId) {
      const fromSiblingName = nameByTypeId.get(spot.spot_type_id);
      return {
        ...spot,
        spot_type_prefix: fromTypeId,
        ...(!hasSpotTypeName(spot) && fromSiblingName ? { spot_type_name: fromSiblingName } : {})
      };
    }

    const layoutMatch = layoutSpotTypeForNames(
      spotTypes,
      spot.spot_type_name,
      spot.status === SpotStatus.NOT_BOOKABLE && !hasSpotTypeName(spot) ? spot.spot_name : undefined
    );
    if (layoutMatch) {
      return {
        ...spot,
        spot_type_prefix: layoutMatch.prefix.trim(),
        ...(!hasSpotTypeName(spot) ? { spot_type_name: layoutMatch.name.trim() } : {})
      };
    }

    if (
      spot.status === SpotStatus.NOT_BOOKABLE &&
      resolveSystemSpotTypeKey(spot.spot_type_id, spot.spot_type_name, undefined) === null
    ) {
      const inferred =
        inferNotBookablePrefixFromDisplayName(spot.spot_type_name, spot.spot_name) ??
        inferNotBookablePrefixFromDisplayName(spot.spot_name, spot.spot_name);
      if (inferred) return { ...spot, spot_type_prefix: inferred };
    }

    return spot;
  });
}
